/**
 * 업로드 전 이미지 축소
 *
 * S3에 원본을 그대로 올리면 화면에서는 72~300px로 줄여 쓰면서도
 * 매번 원본 전체를 내려받는다. 로고 한 장에 수 MB가 걸리는 이유다.
 * 서버 리사이즈가 붙기 전까지는 올리는 쪽에서 줄여두는 편이 확실하다.
 */

/** 원본 비율을 지키면서 한도 안에 들어가는 크기 */
export function fitWithin(width, height, maxWidth, maxHeight) {
  if (!width || !height) return { width: 0, height: 0, scale: 1 };
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    scale,
  };
}

/** 애니메이션·벡터는 캔버스로 다시 그리면 오히려 망가진다 */
const KEEP_AS_IS = ['image/gif', 'image/svg+xml'];

/** 투명 배경을 잃으면 안 되는 형식 (로고가 대부분 여기 해당) */
const HAS_ALPHA = ['image/png', 'image/webp'];

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('이미지를 읽을 수 없습니다.')); };
  img.src = url;
});

const toBlob = (canvas, type, quality) => new Promise((resolve) => {
  if (typeof canvas.toBlob !== 'function') { resolve(null); return; }
  canvas.toBlob(resolve, type, quality);
});

/**
 * @param {File} file
 * @param {{ maxWidth?: number, maxHeight?: number, quality?: number, skipUnderBytes?: number }} [options]
 * @returns {Promise<File>} 축소된 파일. 줄일 수 없거나 실패하면 원본을 그대로 돌려준다.
 */
export async function downscaleImage(file, options = {}) {
  const {
    maxWidth = 1280,
    maxHeight = 1280,
    quality = 0.88,
    skipUnderBytes = 200 * 1024,
  } = options;

  if (!file || !file.type?.startsWith('image/')) return file;
  if (KEEP_AS_IS.includes(file.type)) return file;

  try {
    const img = await loadImage(file);
    const { width, height, scale } = fitWithin(img.naturalWidth, img.naturalHeight, maxWidth, maxHeight);

    // 이미 한도 안이고 용량도 작으면 굳이 다시 인코딩하지 않는다
    if (scale >= 1 && file.size <= skipUnderBytes) return file;
    if (!width || !height) return file;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);

    const type = HAS_ALPHA.includes(file.type) ? 'image/png' : 'image/jpeg';
    const blob = await toBlob(canvas, type, quality);
    if (!blob || blob.size >= file.size) return file;  // 되레 커지면 원본이 낫다

    const name = file.name.replace(/\.[^.]+$/, '') + (type === 'image/png' ? '.png' : '.jpg');
    return new File([blob], name, { type, lastModified: Date.now() });
  } catch {
    return file;  // 축소는 최적화일 뿐이므로 실패해도 업로드는 진행한다
  }
}

/** 용도별 권장 한도 — 화면에서 쓰는 크기의 약 2배(고해상도 디스플레이 대비) */
export const RESIZE_PRESETS = {
  /** 로고: 최대 148px 칸에 표시 */
  logo: { maxWidth: 512, maxHeight: 512, quality: 0.92 },
  /** 목록 썸네일: 최대 300px 카드 */
  thumbnail: { maxWidth: 800, maxHeight: 800, quality: 0.85 },
};
